/**
 * Kreative Neon — Universal Dark Mode & Mosaic Lens Engine
 * Powered by WebGL2 Mosaic Lens (Originkit)
 * High-performance 2-pass shader: Fractal Warp Field + Interactive Mosaic Lens Tiers
 */

(function() {
  'use strict';

  // --- 1. Immediate Theme State Synchronization ---
  function getPreferredTheme() {
    try {
      var saved = localStorage.getItem('kreative_theme_mode');
      if (saved === 'dark' || saved === 'light') return saved;
    } catch(e) {}
    return 'light'; // Default to light mode
  }

  var currentTheme = getPreferredTheme();
  if (currentTheme === 'dark') {
    document.documentElement.setAttribute('data-theme', 'dark');
    if (document.body) document.body.setAttribute('data-theme', 'dark');
  }

  // --- 2. WebGL2 Mosaic Lens Engine ---
  var MosaicLensEngine = (function() {
    var MAX_DPR = 2;
    var LAYERS = 74;
    var GAIN = 0.54;
    var TIERS = 4;

    var VERT_SRC = '#version 300 es\n' +
      'const vec2 P[3] = vec2[3](vec2(-1.0, -1.0), vec2(3.0, -1.0), vec2(-1.0, 3.0));\n' +
      'void main() { gl_Position = vec4(P[gl_VertexID], 0.0, 1.0); }\n';

    var FIELD_SRC = '#version 300 es\n' +
      'precision highp float;\n' +
      'uniform vec2 uRes;\n' +
      'uniform float uTime;\n' +
      'uniform vec3 uC1;\n' +
      'uniform vec3 uC2;\n' +
      'uniform float uSize;\n' +
      'uniform float uAngle;\n' +
      'out vec4 o;\n' +
      'const float TAU = 6.28318530718;\n' +
      'const float LAYERS = ' + LAYERS.toFixed(1) + ';\n' +
      'const float GAIN = ' + GAIN.toFixed(3) + ';\n' +
      'const vec2 CENTRE = vec2(-0.25, -0.56);\n' +
      'const float TILT = -2.2;\n' +
      'const float ZOOM = 1.05;\n' +
      'const float THETA = 2.12;\n' +
      'const float SHEAR = 0.956;\n' +
      'const float SHRINK = 0.949;\n' +
      'const vec2 WARP_FREQ = vec2(0.58, 2.8);\n' +
      'const vec2 WARP_AMP = vec2(0.16, 0.034);\n' +
      'const vec2 ASPECT = vec2(1.65, 0.22);\n' +
      'const float OFFSET = 0.31;\n' +
      'const float GLOW = 0.0021;\n' +
      'const float SOFT = 0.0019;\n' +
      'const float FALLOFF = 0.37;\n' +
      'const float PHASE = 64.0;\n' +
      'const float CYCLE = 0.16;\n' +
      'const float HUE_TRAVEL = 2.0;\n' +
      'mat2 rot(float a) { float c = cos(a), s = sin(a); return mat2(c, s, -s, c); }\n' +
      'void main() {\n' +
      '  vec2 R = uRes;\n' +
      '  vec2 pos = (gl_FragCoord.xy - 0.5 * R) / R.y;\n' +
      '  pos = rot(uAngle) * pos / uSize;\n' +
      '  float t = uTime * 0.49 + PHASE;\n' +
      '  float breath = (-sin(uTime * 0.735) + sin(uTime * 0.49 + 1.0)) * 0.25 + 0.5;\n' +
      '  vec2 u = rot(TILT) * ((pos - CENTRE) * (ZOOM - breath * 0.085));\n' +
      '  mat2 fold = mat2(cos(THETA), sin(THETA), -SHEAR, cos(THETA));\n' +
      '  vec3 col = vec3(0.0);\n' +
      '  for (float i = 1.0; i <= LAYERS; i += 1.0) {\n' +
      '    u.x -= sin(u.y * WARP_FREQ.x + t + i * 0.007) * WARP_AMP.x;\n' +
      '    u.y -= sin(u.x * WARP_FREQ.y - t + i * 0.02) * WARP_AMP.y;\n' +
      '    u = fold * u * SHRINK;\n' +
      '    vec2 q = (u - vec2(OFFSET + breath * 0.1, 0.0)) * ASPECT;\n' +
      '    float g = GLOW / (dot(q, q) + SOFT) * (0.25 + breath * 0.4);\n' +
      '    float r = length(u);\n' +
      '    float k = sin(i * CYCLE + t * 1.2 + r * HUE_TRAVEL) * 0.5 + 0.5;\n' +
      '    col += g * mix(uC1, uC2, k) * (0.62 + 0.5 * k) * exp2(-r * FALLOFF);\n' +
      '  }\n' +
      '  vec3 x = max(col * GAIN, 0.0);\n' +
      '  col = (x * (2.51 * x + 0.03)) / (x * (2.43 * x + 0.59) + 0.14);\n' +
      '  col = pow(clamp(col, 0.0, 1.0), vec3(0.85, 0.92, 0.98));\n' +
      '  col *= 1.0 - smoothstep(0.5, 1.6, length(pos)) * 0.07;\n' +
      '  o = vec4(col, 1.0);\n' +
      '}\n';

    var FINISH_SRC = '#version 300 es\n' +
      'precision highp float;\n' +
      'uniform sampler2D uField;\n' +
      'uniform vec2 uRes;\n' +
      'uniform float uTime;\n' +
      'uniform vec3 uBg;\n' +
      'uniform float uPaper;\n' +
      'uniform vec2 uMouse;\n' +
      'uniform float uOn;\n' +
      'uniform float uReach;\n' +
      'uniform float uTile;\n' +
      'out vec4 o;\n' +
      'const float TIERS = ' + TIERS.toFixed(1) + ';\n' +
      'float ign(vec2 p, float f) { p += 5.588238 * mod(f, 64.0); return fract(52.9829189 * fract(0.06711056 * p.x + 0.00583715 * p.y)); }\n' +
      'vec3 scene(vec2 uv) { return texture(uField, clamp(uv, 0.0, 1.0)).rgb; }\n' +
      'vec3 mosaic(vec2 frag, vec2 uv) {\n' +
      '  float base = uTile;\n' +
      '  float tier = 0.0;\n' +
      '  if (uOn > 1e-4) {\n' +
      '    vec2 cc = (floor(frag / base) + 0.5) * base;\n' +
      '    vec2 d = (cc - uMouse) / uReach;\n' +
      '    float w = uOn * exp(-dot(d, d));\n' +
      '    tier = floor(min(w * (TIERS + 1.0), TIERS));\n' +
      '  }\n' +
      '  if (tier >= TIERS) return scene(uv);\n' +
      '  float cell = base / exp2(tier);\n' +
      '  vec2 c = (floor(frag / cell) + 0.5) * cell;\n' +
      '  vec3 s = scene((c + cell * vec2(-0.25, -0.25)) / uRes) + scene((c + cell * vec2(0.25, -0.25)) / uRes)\n' +
      '         + scene((c + cell * vec2(-0.25, 0.25)) / uRes) + scene((c + cell * vec2(0.25, 0.25)) / uRes);\n' +
      '  return s * 0.25;\n' +
      '}\n' +
      'void main() {\n' +
      '  vec2 frag = gl_FragCoord.xy;\n' +
      '  vec3 L = max(mosaic(frag, frag / uRes), 0.0);\n' +
      '  vec3 dark = uBg + L * (1.0 - uBg);\n' +
      '  float strength = clamp(max(L.r, max(L.g, L.b)), 0.0, 1.0);\n' +
      '  vec3 paper = uBg * (1.0 - strength) + L * 0.96;\n' +
      '  vec3 col = mix(dark, paper, uPaper);\n' +
      '  col += (ign(frag, floor(uTime * 24.0)) - 0.5) / 255.0;\n' +
      '  o = vec4(clamp(col, 0.0, 1.0), 1.0);\n' +
      '}\n';

    var PRESET = {
      background: "#000000",
      color1: "#29B6F6",
      color2: "#B04DFF",
      speed: 100,
      size: 50,
      angle: 139,
      tileSize: 6,
      hover: 200,
      reach: 658
    };

    function parseColor(s) {
      if (!s) return [0, 0, 0];
      s = s.trim();
      if (s.charAt(0) === '#') {
        var h = s.slice(1);
        if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
        if (h.length >= 6) {
          return [
            parseInt(h.slice(0, 2), 16) / 255,
            parseInt(h.slice(2, 4), 16) / 255,
            parseInt(h.slice(4, 6), 16) / 255
          ];
        }
      }
      return [0, 0, 0];
    }

    function clampN(v, lo, hi) {
      return v < lo ? lo : v > hi ? hi : v;
    }

    var gl = null;
    var canvas = null;
    var fieldProg = null;
    var finishProg = null;
    var vao = null;
    var target = null;
    var uf = {};
    var un = {};
    var isRunning = false;
    var raf = 0;
    var last = -1;
    var clock = 0;

    var ptr = { tx: window.innerWidth / 2, ty: window.innerHeight / 2, inside: true };
    var mx = window.innerWidth / 2;
    var my = window.innerHeight / 2;
    var on = 0.5;

    function linkShader(frag, label) {
      var shader = function(type, src) {
        var sh = gl.createShader(type);
        if (!sh) return null;
        gl.shaderSource(sh, src);
        gl.compileShader(sh);
        if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
          console.error('MosaicLens ' + label + ' shader:', gl.getShaderInfoLog(sh));
          gl.deleteShader(sh);
          return null;
        }
        return sh;
      };
      var vs = shader(gl.VERTEX_SHADER, VERT_SRC);
      var fs = shader(gl.FRAGMENT_SHADER, frag);
      if (!vs || !fs) return null;
      var prog = gl.createProgram();
      if (!prog) return null;
      gl.attachShader(prog, vs);
      gl.attachShader(prog, fs);
      gl.linkProgram(prog);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
        console.error('MosaicLens ' + label + ' link:', gl.getProgramInfoLog(prog));
        gl.deleteProgram(prog);
        return null;
      }
      return prog;
    }

    function createFieldTarget() {
      var fbo = gl.createFramebuffer();
      var tex = null;
      var w = 0, h = 0;
      var half = !!gl.getExtension('EXT_color_buffer_float');
      return {
        fbo: fbo,
        texture: function() { return tex; },
        width: function() { return w; },
        height: function() { return h; },
        resize: function(nw, nh) {
          if (nw === w && nh === h && tex) return;
          for (var attempt = 0; attempt < 2; attempt++) {
            if (tex) gl.deleteTexture(tex);
            tex = gl.createTexture();
            gl.bindTexture(gl.TEXTURE_2D, tex);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
            gl.texImage2D(gl.TEXTURE_2D, 0, half ? gl.RGBA16F : gl.RGBA8, nw, nh, 0, gl.RGBA, half ? gl.HALF_FLOAT : gl.UNSIGNED_BYTE, null);
            gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
            gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
            var ok = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
            gl.bindFramebuffer(gl.FRAMEBUFFER, null);
            if (ok || !half) break;
            half = false;
          }
          w = nw;
          h = nh;
        },
        dispose: function() {
          if (tex) gl.deleteTexture(tex);
          gl.deleteFramebuffer(fbo);
        }
      };
    }

    function init() {
      canvas = document.getElementById('kreative-dark-mosaic-canvas') || document.getElementById('kreative-dark-embergrid-canvas');
      if (!canvas) return false;

      try {
        gl = canvas.getContext('webgl2', { antialias: false, alpha: false, depth: false, stencil: false });
      } catch(e) {}

      if (!gl) {
        console.warn('MosaicLens: WebGL2 unavailable');
        return false;
      }

      fieldProg = linkShader(FIELD_SRC, 'field');
      finishProg = linkShader(FINISH_SRC, 'finish');
      if (!fieldProg || !finishProg) return false;

      var ufNames = ['uRes', 'uTime', 'uC1', 'uC2', 'uSize', 'uAngle'];
      for (var i = 0; i < ufNames.length; i++) {
        uf[ufNames[i]] = gl.getUniformLocation(fieldProg, ufNames[i]);
      }

      var unNames = ['uField', 'uRes', 'uTime', 'uBg', 'uPaper', 'uMouse', 'uOn', 'uReach', 'uTile'];
      for (var j = 0; j < unNames.length; j++) {
        un[unNames[j]] = gl.getUniformLocation(finishProg, unNames[j]);
      }

      vao = gl.createVertexArray();
      gl.bindVertexArray(vao);

      target = createFieldTarget();

      // Track pointer across window
      function onPointer(e) {
        ptr.tx = e.clientX;
        ptr.ty = e.clientY;
        ptr.inside = true;
      }

      function onTouch(e) {
        if (e.touches && e.touches[0]) {
          ptr.tx = e.touches[0].clientX;
          ptr.ty = e.touches[0].clientY;
          ptr.inside = true;
        }
      }

      window.addEventListener('pointermove', onPointer, { passive: true });
      window.addEventListener('pointerdown', onPointer, { passive: true });
      window.addEventListener('touchmove', onTouch, { passive: true });
      window.addEventListener('touchstart', onTouch, { passive: true });
      document.addEventListener('pointerleave', function() { ptr.inside = false; }, { passive: true });
      window.addEventListener('scroll', function() { ptr.inside = true; }, { passive: true });

      return true;
    }

    function render(now) {
      if (!isRunning) return;
      raf = requestAnimationFrame(render);

      var dt = last < 0 ? 0 : clampN((now - last) / 1000, 0, 0.05);
      last = now;

      var vSpeed = clampN(PRESET.speed, 0, 100) / 50;
      var vSize = clampN(PRESET.size, 50, 200) / 100;
      var vAngle = (clampN(PRESET.angle, -180, 180) * Math.PI) / 180;
      var vTile = clampN(Math.round(PRESET.tileSize), 6, 48);
      var vHover = clampN(PRESET.hover, 0, 200) / 100;
      var vReach = clampN(PRESET.reach, 10, 800);

      clock = (clock + dt * vSpeed) % 3600;

      var dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      var cw = window.innerWidth;
      var ch = window.innerHeight;
      var bw = Math.max(1, Math.round(cw * dpr));
      var bh = Math.max(1, Math.round(ch * dpr));

      if (canvas.width !== bw || canvas.height !== bh) {
        canvas.width = bw;
        canvas.height = bh;
      }
      target.resize(Math.max(1, Math.round(bw / 2)), Math.max(1, Math.round(bh / 2)));

      var present = ptr.inside ? 1 : 0;
      if (present && on < 0.02) {
        mx = ptr.tx;
        my = ptr.ty;
      }
      on += (present - on) * (1 - Math.exp(-dt * 5));
      var k = 1 - Math.exp(-dt * 16);
      mx += (ptr.tx - mx) * k;
      my += (ptr.ty - my) * k;

      var c1 = parseColor(PRESET.color1);
      var c2 = parseColor(PRESET.color2);
      var bg = parseColor(PRESET.background);
      var bgLum = 0.2126 * bg[0] + 0.7152 * bg[1] + 0.0722 * bg[2];

      var sx = bw / cw;
      var sy = bh / ch;

      // Pass 1: Render Fractal Warp Field to FBO
      gl.bindFramebuffer(gl.FRAMEBUFFER, target.fbo);
      gl.viewport(0, 0, target.width(), target.height());
      gl.useProgram(fieldProg);
      gl.uniform2f(uf.uRes, target.width(), target.height());
      gl.uniform1f(uf.uTime, clock);
      gl.uniform3f(uf.uC1, c1[0], c1[1], c1[2]);
      gl.uniform3f(uf.uC2, c2[0], c2[1], c2[2]);
      gl.uniform1f(uf.uSize, vSize);
      gl.uniform1f(uf.uAngle, vAngle);
      gl.drawArrays(gl.TRIANGLES, 0, 3);

      // Pass 2: Interactive Mosaic Lens with Dynamic Cursor Tiers
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, bw, bh);
      gl.useProgram(finishProg);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, target.texture());
      gl.uniform1i(un.uField, 0);
      gl.uniform2f(un.uRes, bw, bh);
      gl.uniform1f(un.uTime, clock);
      gl.uniform3f(un.uBg, bg[0], bg[1], bg[2]);
      gl.uniform1f(un.uPaper, clampN((bgLum - 0.35) / 0.3, 0, 1));

      gl.uniform2f(un.uMouse, mx * sx, bh - my * sy);
      gl.uniform1f(un.uOn, on * vHover);
      gl.uniform1f(un.uReach, vReach * sy);
      gl.uniform1f(un.uTile, Math.max(1, Math.round(vTile * dpr)));
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    return {
      start: function() {
        if (!gl) {
          if (!init()) {
            if (document.readyState === 'loading') {
              document.addEventListener('DOMContentLoaded', function() {
                if (currentTheme === 'dark') MosaicLensEngine.start();
              }, { once: true });
            }
            return;
          }
        }
        if (isRunning) return;
        isRunning = true;
        last = performance.now();
        raf = requestAnimationFrame(render);
      },
      stop: function() {
        isRunning = false;
        if (raf) cancelAnimationFrame(raf);
        raf = 0;
      }
    };
  })();

  // --- 3. Heading Neon Glow Enhancer ---
  function enhanceProminentHeadings() {
    try {
      var headingSelectors = [
        'h1', 'h2',
        '.section-header__title',
        '.product-single__title',
        '.collection-hero__title',
        '.page-title',
        '.hero__title',
        '.usp-title',
        '.shop-title',
        '.biz-title',
        '.designer-main-title',
        '.quote-main-title',
        '.category-heading',
        '.pillar-title',
        '.benefits-title',
        '.reviews-title',
        '.lookbook-title',
        '.illuminated-title',
        '.brands-title'
      ];
      var headings = document.querySelectorAll(headingSelectors.join(','));
      headings.forEach(function(h) {
        if (!h.classList.contains('k-prominent-heading')) {
          h.classList.add('k-prominent-heading');
        }
        var strongs = h.querySelectorAll('strong, b');
        strongs.forEach(function(st) {
          if (!st.classList.contains('neon-glow-word')) {
            st.classList.add('neon-glow-word');
          }
        });
      });
    } catch(e) {}
  }

  // --- 4. UI Theme Toggle Manager ---
  function applyTheme(mode) {
    currentTheme = mode;
    try {
      localStorage.setItem('kreative_theme_mode', mode);
    } catch(e) {}

    if (mode === 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark');
      if (document.body) document.body.setAttribute('data-theme', 'dark');
      MosaicLensEngine.start();
    } else {
      document.documentElement.removeAttribute('data-theme');
      if (document.body) document.body.removeAttribute('data-theme');
      MosaicLensEngine.stop();
    }

    // Update all toggle buttons in DOM
    var toggles = document.querySelectorAll('.js-theme-toggle');
    toggles.forEach(function(btn) {
      if (mode === 'dark') {
        btn.classList.add('is-dark');
        btn.setAttribute('aria-checked', 'true');
        btn.setAttribute('title', 'Switch to Light Mode');
      } else {
        btn.classList.remove('is-dark');
        btn.setAttribute('aria-checked', 'false');
        btn.setAttribute('title', 'Switch to Dark Mode');
      }
    });

    enhanceProminentHeadings();
  }

  function toggleTheme() {
    var newMode = (currentTheme === 'dark') ? 'light' : 'dark';
    applyTheme(newMode);
  }

  // Expose on window immediately so other scripts and tests can access it
  window.KreativeTheme = {
    getTheme: function() { return currentTheme; },
    setTheme: applyTheme,
    toggle: toggleTheme,
    enhanceHeadings: enhanceProminentHeadings
  };

  // Bind global click listener immediately (works at any point in page lifecycle)
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('.js-theme-toggle');
    if (btn) {
      e.preventDefault();
      toggleTheme();
    }
  });

  // Initialize theme UI as soon as possible
  function initThemeUI() {
    applyTheme(currentTheme);
    enhanceProminentHeadings();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initThemeUI);
  } else {
    initThemeUI();
  }

})();
